import { useState, useRef, useEffect } from 'react';

export interface SelectOption {
  label: string;
  value: string;
}

interface SelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Width/utility classes for the root. Defaults to a fixed `w-52` (filter use). */
  className?: string;
}

export function Select({
  options,
  value,
  onChange,
  placeholder = 'Selecionar...',
  className = 'w-52',
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find((o) => o.value === value);

  const filtered = options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()));

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else setQuery('');
  }, [open]);

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="border-line bg-surface text-on-surface hover:border-line-strong flex w-full items-center justify-between gap-2 rounded-md border px-4 py-2 text-sm font-medium transition-all duration-200"
      >
        <span className="truncate">{selected?.label ?? placeholder}</span>
        <span
          className={`material-symbols-outlined text-on-surface-light flex-shrink-0 text-[16px] transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        >
          expand_more
        </span>
      </button>

      {open && (
        <div className="bg-surface border-line absolute z-50 mt-1.5 w-full overflow-hidden rounded-md border shadow-lg">
          <div className="border-line border-b px-2 pt-2 pb-1.5">
            <div className="border-line bg-background flex items-center gap-2 rounded-md border px-2 py-1.5">
              <span className="material-symbols-outlined text-on-surface-light text-[14px]">
                search
              </span>
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filtrar..."
                className="text-on-surface placeholder:text-on-surface-light/70 flex-1 bg-transparent text-xs outline-none"
              />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-on-surface-light px-3 py-3 text-xs">Sem resultados</p>
            ) : (
              filtered.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm transition-colors duration-150 ${
                    opt.value === value
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-on-surface-light hover:bg-background hover:text-on-surface'
                  }`}
                >
                  <span
                    className={`h-1.5 w-1.5 flex-shrink-0 rounded-full transition-opacity ${opt.value === value ? 'bg-primary opacity-100' : 'opacity-0'}`}
                  />
                  {opt.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
